import { Dispatch, MouseEvent, ReactElement, ReactNode, RefObject, SetStateAction } from 'react';
import type { ConfigValidations, ConfigValidationsType, Fields } from '@/hooks/useValidator/interfaces';
import { VALIDITY } from '@/constants';
import { RenderRTE } from '@/components-2.0/structure/RenderRTE';
import type { InputProps } from '@/ui-components/atoms/Inputs/Input/Input';
import { FlexibleFormAlert, type FlexibleFormAlertTone } from './FlexibleFormAlert';
import { getFlexibleFormFieldMetadata, normalizeFlexibleFormFieldKey } from './flexibleFormFieldMatching';
import {
    isPromoCodeFormField,
    PROMO_CODE_REQUIRED_ERROR,
    sanitizePromoCode,
} from './promoCodeEntryValidation';

export interface ServerFields {
    error: string;
    message: string;
    name: string;
}

interface FormSubmitResponseJson {
    status?: unknown;
    errors?: unknown;
    formSubmitResponse?: unknown;
}

export interface ParseFormSubmitResponseOptions {
    sweepstakesEnded?: boolean;
}

export interface ParsedFormSubmitResponse {
    serverValidations: Fields;
    submitState?: SubmitState;
    sweepstakesEnded: boolean;
}

export const DUPLICATE_ENTRY_ERROR_MESSAGE = 'Sorry, only one entry per person allowed.';

export const SMS_OPT_IN_FIELD_NAME = 'smsOptIn';

export function getSmsOptInCheckboxClickHandler(fieldName: string) {
    if (fieldName !== SMS_OPT_IN_FIELD_NAME) return undefined;

    return (e: MouseEvent<HTMLInputElement>) => {
        e.currentTarget.setAttribute(
            'data-linkname',
            e.currentTarget.checked ? 'SMS Opt-In Click Off' : 'SMS Opt-In Click On',
        );
    };
}

export function getSmsOptInCheckboxAdditionalProps(fieldName: string) {
    if (fieldName !== SMS_OPT_IN_FIELD_NAME) return undefined;

    return { 'data-linkname': 'SMS Opt-In Click On' };
}

export type ValidationConfig = Record<string, Array<ConfigValidations>>;
export type SubmitState = 'untouched' | 'failed' | 'success' | 'error' | 'duplicate';

export interface FlexibleFormSubmitContext {
    validations: Fields;
    setValidations: Dispatch<SetStateAction<Fields>>;
    submitState: SubmitState;
    setSubmitState: Dispatch<SetStateAction<SubmitState>>;
}

export interface FlexibleFormStyles {
    flexForm: string;
    halfWidth: string;
    fullWidth: string;
    formSectionHeaderStyles: string;
    formSectionTextBlockStyles?: string;
    inputStyles?: string;
    dropdownSelectStyles?: string;
    wrapperRow: string;
    stateContainer: string;
    stateWrapper: string;
    zipInputStyles: string;
    selectorStyles?: string;
}

export interface SelectHeaderConfig {
    title: ReactNode;
    body: ReactNode;
}

export interface FlexibleFieldInputProps {
    inputMode?: InputProps['inputMode'];
    maxLength?: number;
    transformValue?: (value: string) => string;
}

const RADIO_ID_VIN_MAX_LENGTH = 17;
const ZIP_DISPLAY_MAX_LENGTH = 10;
const ZIP_SUBMIT_MAX_LENGTH = 9;
const LICENSE_PLATE_TYPE_KEYS = new Set(['licenseplate', 'licenseplatenumber']);
const RADIO_ID_VIN_INVALID_MESSAGE = <p>{'Enter 8 to 17 letters or numbers.'}</p>;

function isZipField(fieldType: string, fieldName: string) {
    const normalizedFieldType = normalizeFlexibleFormFieldKey(fieldType);
    const normalizedFieldName = normalizeFlexibleFormFieldKey(fieldName);
    return normalizedFieldType === 'zipcode' || normalizedFieldName === 'zip' || normalizedFieldName === 'zipcode';
}

function isRadioIdVinField(fieldType: string) {
    return normalizeFlexibleFormFieldKey(fieldType) === 'radioidvin';
}

function isLicensePlateField(fieldType: string, fieldName: string) {
    const normalizedFieldType = normalizeFlexibleFormFieldKey(fieldType);
    const normalizedFieldName = normalizeFlexibleFormFieldKey(fieldName);
    return LICENSE_PLATE_TYPE_KEYS.has(normalizedFieldType) || LICENSE_PLATE_TYPE_KEYS.has(normalizedFieldName);
}

function isLicensePlateFormField(formField: SiteFormField) {
    const { type, fieldName, displayName, label } = getFlexibleFormFieldMetadata(formField);

    return (
        LICENSE_PLATE_TYPE_KEYS.has(type) ||
        LICENSE_PLATE_TYPE_KEYS.has(fieldName) ||
        displayName.includes('licenseplate') ||
        label.includes('licenseplate')
    );
}

export function isLicensePlateFormAsset(formAsset: SiteFlexibleForm) {
    return formAsset.fields.form_fields.some(isLicensePlateFormField);
}

export function sanitizeAlphanumeric(value: string) {
    return value.replace(/[^A-Za-z0-9]/g, '');
}

export function getFlexibleFieldInputProps(formField: SiteFormField): FlexibleFieldInputProps {
    const fieldType = formField.fields.type;
    const fieldName = formField.fields.field_name;

    if (isRadioIdVinField(fieldType)) {
        return {
            maxLength: RADIO_ID_VIN_MAX_LENGTH,
            transformValue: sanitizeAlphanumeric,
        };
    }

    if (isZipField(fieldType, fieldName)) {
        return {
            inputMode: 'numeric',
            maxLength: ZIP_DISPLAY_MAX_LENGTH,
        };
    }

    if (isLicensePlateFormField(formField)) {
        return {
            transformValue: sanitizeAlphanumeric,
        };
    }

    if (isPromoCodeFormField(formField)) {
        return {
            transformValue: sanitizePromoCode,
        };
    }

    return {};
}

export function getValidationMessageType(
    fieldConfig: Array<ConfigValidations> | undefined,
    fieldValidation: Fields[string] | undefined,
    fallback: ConfigValidationsType = 'required',
) {
    const failedValidation = fieldConfig?.find(({ type }) => fieldValidation?.validations?.[type] === false);

    if (failedValidation) {
        return failedValidation.type;
    }

    if (fieldConfig?.[0]) {
        return fieldConfig[0].type;
    }

    return fallback;
}

export function createValidationConfigFields(
    formFields: SiteFormField[],
    emailRef: RefObject<HTMLInputElement | null>,
    confirmEmailRef: RefObject<HTMLInputElement | null>,
) {
    const fields: ValidationConfig = {};
    let hasStateAndZipField = false;

    formFields.forEach((formField: SiteFormField) => {
        const helperText = <RenderRTE contentfulDoc={formField.fields.error_message} />;
        const fieldName = formField.fields.field_name;
        const fieldType = formField.fields.type.toLowerCase();
        const required = formField.fields.required;

        if (fieldType === 'header' || fieldType === 'text block') {
            return;
        }

        if (fieldType === 'state and zip') {
            hasStateAndZipField = true;
        }

        if (fieldType === 'email only' && required) {
            fields[fieldName] = [{ type: 'email', helperText, validateAlso: [confirmEmailRef.current] }];
        } else if (fieldType === 'email/confirmation' && required) {
            fields[fieldName] = [{ type: 'matchesCaseInsensitive', helperText, config: emailRef.current }];
        } else if (fieldType === 'phone' && required) {
            fields[fieldName] = [{ type: 'minlength', config: 14, helperText }];
        } else if (isZipField(fieldType, fieldName)) {
            fields[fieldName] = [];
            if (required) {
                fields[fieldName].push({ type: 'required', helperText });
            }
            fields[fieldName].push({ type: 'zipCode', helperText });
        } else if (isRadioIdVinField(fieldType)) {
            fields[fieldName] = [];
            if (required) {
                fields[fieldName].push({ type: 'required', helperText });
            }
            fields[fieldName].push({ type: 'radioIdVin', helperText: RADIO_ID_VIN_INVALID_MESSAGE });
        } else if (isLicensePlateFormField(formField)) {
            fields[fieldName] = [];
            if (required) {
                fields[fieldName].push({ type: 'required', helperText });
            }
            fields[fieldName].push({ type: 'alphanumeric', helperText });
        } else if (isPromoCodeFormField(formField)) {
            fields[fieldName] = [];
            if (required) {
                fields[fieldName].push({ type: 'required', helperText: PROMO_CODE_REQUIRED_ERROR });
            }
            fields[fieldName].push({
                type: 'regex',
                config: '^[A-Za-z0-9-]+$',
                helperText: PROMO_CODE_REQUIRED_ERROR,
            });
        } else if ((fieldType === 'checkbox' || fieldType === 'checkbox legal') && required) {
            fields[fieldName] = [{ type: 'istrue', helperText }];
        } else if (fieldType === 'radio button' && required) {
            fields[fieldName] = [{ type: 'istrue', helperText }];
        } else if (required) {
            fields[fieldName] = [{ type: 'required', helperText }];
        }

        const { validation_dropdown, validation_value, validation_error } = formField.fields;
        if (!isPromoCodeFormField(formField) && validation_dropdown?.toLowerCase() === 'regex' && validation_value) {
            const regexHelperText = validation_error ? <RenderRTE contentfulDoc={validation_error} /> : helperText;
            (fields[fieldName] ??= []).push({ type: 'regex', config: validation_value, helperText: regexHelperText });
        }
    });

    if (hasStateAndZipField && !fields.zip) {
        fields.zip = [
            { type: 'required', helperText: <p>{'Valid zip code required'}</p> },
            { type: 'zipCode', helperText: <p>{'Valid zip code required'}</p> },
        ];
    }

    return fields;
}

export function normalizeFormData(data: FormData, sourceName?: string) {
    Array.from(data.entries()).forEach(([fieldName, fieldValue]) => {
        if (typeof fieldValue !== 'string') {
            return;
        }

        if (fieldName === 'phone' && fieldValue.length > 0) {
            data.set(fieldName, fieldValue.replace(/\D/g, ''));
            return;
        }

        if (isZipField(fieldName, fieldName) && fieldValue.length > 0) {
            data.set(fieldName, fieldValue.replace(/\D/g, '').substring(0, ZIP_SUBMIT_MAX_LENGTH));
            return;
        }

        if (isRadioIdVinField(fieldName) || isLicensePlateField(fieldName, fieldName)) {
            data.set(fieldName, sanitizeAlphanumeric(fieldValue));
            return;
        }

        if (normalizeFlexibleFormFieldKey(fieldName) === 'promocode') {
            data.set(fieldName, sanitizePromoCode(fieldValue));
            return;
        }

        if (normalizeFlexibleFormFieldKey(fieldName) === 'state') {
            data.set(fieldName, fieldValue.trim());
        }
    });

    data.set('languagePreference', 'en-US');

    if (sourceName) {
        data.set('sourceName', sourceName);
    }

    return data;
}

export function wrapFormElements(elements: ReactNode[], wrapperRowClassName: string) {
    const result: ReactNode[] = [];

    for (let i = 0; i < elements.length; i++) {
        const current = elements[i] as ReactElement<{ wrapperId?: string }> | undefined;
        const next = elements[i + 1] as ReactElement<{ wrapperId?: string }> | undefined;

        if (current?.props?.wrapperId === 'halfWidth' && next?.props?.wrapperId === 'halfWidth') {
            result.push(
                <div className={wrapperRowClassName} key={`wrap-${i}`}>
                    {current}
                    {next}
                </div>,
            );
            i++;
        } else if (current) {
            result.push(current);
        }
    }

    return result;
}

export function buildServerValidation(validations: Fields, fieldName: string) {
    const existingValidation = validations[fieldName];
    if (!existingValidation) {
        return undefined;
    }

    return {
        value: existingValidation.value,
        invalid: VALIDITY.invalid,
        validations: { ...existingValidation.validations },
        helperText: { ...existingValidation.helperText },
    };
}

function isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null;
}

function getServerResponseStatus(json: FormSubmitResponseJson) {
    if (isRecord(json.formSubmitResponse) && isRecord(json.formSubmitResponse.status)) {
        const nestedStatus = json.formSubmitResponse.status.message;
        if (typeof nestedStatus === 'string') {
            return nestedStatus;
        }
    }

    return typeof json.status === 'string' ? json.status : undefined;
}

function getServerResponseFields(json: FormSubmitResponseJson): ServerFields[] {
    const response = isRecord(json.formSubmitResponse) ? json.formSubmitResponse : json;
    if (!isRecord(response.errors) || !Array.isArray(response.errors.fields)) {
        return [];
    }

    return response.errors.fields.filter(
        (field): field is ServerFields =>
            isRecord(field) &&
            typeof field.name === 'string' &&
            typeof field.error === 'string' &&
            typeof field.message === 'string',
    );
}

export function parseFormSubmitResponse(
    json: unknown,
    validations: Fields,
    options: ParseFormSubmitResponseOptions = {},
): ParsedFormSubmitResponse {
    const result: ParsedFormSubmitResponse = {
        serverValidations: {},
        sweepstakesEnded: false,
    };

    if (!isRecord(json)) {
        return result;
    }

    const status = getServerResponseStatus(json);
    if (status === 'SUCCESS') {
        result.submitState = 'success';
        return result;
    }

    if (status !== 'ERROR') {
        return result;
    }

    let surfacedError = false;

    getServerResponseFields(json).forEach((field) => {
        const serverValidation = buildServerValidation(validations, field.name);
        if (!serverValidation) return;

        result.serverValidations[field.name] = serverValidation;
        surfacedError = true;

        if (field.name === 'form') {
            if (field.error === 'unknown') {
                result.submitState = 'failed';
            } else if (field.error === 'invalid' && field.message === 'duplicate') {
                result.serverValidations.form = {
                    value: '',
                    invalid: VALIDITY.invalid,
                    validations: { formerror: true },
                    helperText: { formerror: DUPLICATE_ENTRY_ERROR_MESSAGE },
                };
                result.submitState = 'duplicate';
            } else if (options.sweepstakesEnded && field.error === 'invalid' && field.message === 'ended') {
                result.sweepstakesEnded = true;
            }
        } else {
            delete result.serverValidations.form;
        }
    });

    if (!surfacedError) {
        result.submitState = 'failed';
    }

    return result;
}

export function createFlexibleFormSubmitErrorValidation(
    validations: Fields,
    message: ReactNode,
    tone: FlexibleFormAlertTone = 'warning',
) {
    return {
        ...validations,
        form: {
            value: validations.form.value || '',
            invalid: VALIDITY.invalid,
            validations: { ...validations.form.validations, formerror: true },
            helperText: {
                ...validations.form.helperText,
                formerror: <FlexibleFormAlert tone={tone} message={message} />,
            },
        },
    };
}

export function setFlexibleFormSubmitError(
    { validations, setValidations, setSubmitState }: FlexibleFormSubmitContext,
    message: ReactNode,
    tone: FlexibleFormAlertTone = 'warning',
) {
    setValidations(createFlexibleFormSubmitErrorValidation(validations, message, tone));
    setSubmitState('untouched');
}

export function clearFlexibleFormSubmitError({ validations, setValidations }: FlexibleFormSubmitContext) {
    setValidations({
        ...validations,
        form: {
            ...validations.form,
            invalid: VALIDITY.valid,
            validations: {},
            helperText: {},
        },
    });
}
